import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "../../../components/ui/Sidebar";
import Sidebar from "../../../components/ui/Sidebar";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import toast from "react-hot-toast";
import { useCreateExpense } from "./useCreateExpense";
import { useGetVendors } from "pages/vendor-management/useGetVendors";

// ── Constants ─────────────────────────────────────────────────────────────────
const CATEGORIES = [
  "Administrative",
  "Advertisement",
  "Fees",
  "Office Expense",
  "Rent",
  "Salaries",
  "Utilities",
  "Other",
];

const IMPORT_SUBTYPES = [
  { value: "ec_good", label: "EC (Good)" },
  { value: "ec_service", label: "EC (Service)" },
  { value: "non_ec", label: "Non EC" },
];

const VAT_OPTIONS = [
  { label: "0% (Exempt)", value: 0 },
  { label: "15% (Standard)", value: 15 },
  { label: "Custom", value: "custom" },
];

const EMPTY_LINE = () => ({
  id: Date.now() + Math.random(),
  description: "",
  qty: 1,
  price: "",
  discountType: "percent",
  discount: "",
  vatRate: 15,
  customVat: "",
  showCustomVat: false,
});

const EMPTY_FORM = {
  vendorId: "",
  vendorName: "",
  category: "",
  billNumber: "BILL-001",
  billDate: new Date().toISOString().split("T")[0],
  expenseType: "domestic",
  importSubtype: "",
  lines: [],
  notes: "",
};

// ── Calc ──────────────────────────────────────────────────────────────────────
const calcLine = (line) => {
  const qty = parseFloat(line.qty) || 0;
  const price = parseFloat(line.price) || 0;
  const discount = parseFloat(line.discount) || 0;
  const vatRate = line.showCustomVat
    ? parseFloat(line.customVat) || 0
    : parseFloat(line.vatRate) || 0;

  const subtotal = qty * price;
  const discountAmount =
    line.discountType === "percent"
      ? subtotal * (discount / 100)
      : Math.min(discount, subtotal);
  const afterDiscount = Math.max(0, subtotal - discountAmount);
  const vatAmount = afterDiscount * (vatRate / 100);
  const total = afterDiscount + vatAmount;
  return { subtotal, discountAmount, afterDiscount, vatAmount, total, vatRate };
};

// ── Re-usable components ──────────────────────────────────────────────────────
const SectionCard = ({
  icon,
  iconBg,
  iconColor,
  title,
  subtitle,
  defaultExpanded = true,
  action,
  children,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}
            >
              <Icon name={icon} size={20} className={iconColor} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground dark:text-white">
                {title}
              </h3>
              <p className="text-sm text-muted-foreground dark:text-gray-300">
                {subtitle}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {action}
            <Button
              variant="ghost"
              size="sm"
              iconName={expanded ? "ChevronUp" : "ChevronDown"}
              onClick={() => setExpanded(!expanded)}
            />
          </div>
        </div>
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const TextInput = ({ error, className = "", ...props }) => (
  <input
    {...props}
    className={`w-full px-3 py-2.5 border rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${
      error ? "border-red-400" : "border-gray-200 dark:border-gray-600"
    } ${className}`}
  />
);

const SelectInput = ({ children, ...props }) => (
  <select
    {...props}
    className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
  >
    {children}
  </select>
);

const Field = ({ label, required, error, hint, children }) => (
  <div>
    {label && (
      <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
    )}
    {children}
    {hint && !error && (
      <p className="text-xs text-muted-foreground mt-1">{hint}</p>
    )}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

// ── Bill Wizard Content ───────────────────────────────────────────────────────
const PurchaseBillContent = () => {
  const { isCollapsed } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("pages");
  const existing = location.state?.expense || null;

  const [form, setForm] = useState({ ...EMPTY_FORM, lines: [EMPTY_LINE()] });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const createExpense = useCreateExpense();
  const { data: vendors = [] } = useGetVendors();

  useEffect(() => {
    if (existing) {
      setForm({
        ...EMPTY_FORM,
        ...existing,
        lines: existing.lines?.length ? existing.lines : [EMPTY_LINE()],
      });
    }
  }, [existing]);

  const set = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  // Line helpers
  const addLine = () =>
    setForm((p) => ({ ...p, lines: [...p.lines, EMPTY_LINE()] }));
  const removeLine = (id) =>
    setForm((p) => ({ ...p, lines: p.lines.filter((l) => l.id !== id) }));
  const updateLine = (id, field, value) =>
    setForm((p) => ({
      ...p,
      lines: p.lines.map((l) => {
        if (l.id !== id) return l;
        const u = { ...l, [field]: value };
        if (field === "vatRate") {
          u.showCustomVat = value === "custom";
          if (value !== "custom") u.customVat = "";
        }
        return u;
      }),
    }));

  // Totals
  const lineCalcs = form.lines.map(calcLine);
  const total = lineCalcs.reduce((s, c) => s + c.total, 0);
  const totalVat = lineCalcs.reduce((s, c) => s + c.vatAmount, 0);
  const totalDisc = lineCalcs.reduce((s, c) => s + c.discountAmount, 0);
  const netAmount = total;

  const validate = () => {
    const e = {};
    if (!form.vendorId) e.vendorId = "Vendor is required.";
    if (!form.billNumber.trim()) e.billNumber = "Bill number is required.";
    if (!form.billDate) e.billDate = "Bill date is required.";
    if (!form.category) e.category = "Category is required.";
    if (form.expenseType === "import" && !form.importSubtype)
      e.importSubtype = "Please select import subtype.";
    if (!form.lines.length) e.lines = "At least one line item is required.";
    form.lines.forEach((l, i) => {
      if (!l.description.trim()) e[`desc_${i}`] = "Description required.";
      if (!l.price || parseFloat(l.price) <= 0)
        e[`price_${i}`] = "Price required.";
    });
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      toast.error(t("purchaseExpenseForms.saveError"));
      return;
    }

    setSaving(true);
    try {
      const lineItems = form.lines.map((line) => {
        const c = calcLine(line);
        return {
          description: line.description.trim(),
          quantity: parseFloat(line.qty) || 0,
          unitPrice: parseFloat(line.price) || 0,
          discountAmount: c.discountAmount, // absolute, resolved from % or fixed
          taxRate: c.vatRate,
        };
      });

      await createExpense.mutateAsync({
        documentType: "bill",
        vendorId: form.vendorId,
        vendorName: form.vendorName.trim(),
        category: form.category,
        documentDate: form.billDate,
        vendorBillNumber: form.billNumber,
        notes: form.notes || "",
        lineItems,
      });

      toast.success(t("purchaseExpenseForms.purchaseBillRecorded"));
      navigate("/expenses");
    } catch (err) {
      toast.error(err?.message || t("purchaseExpenseForms.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n) => n.toLocaleString("en-SA", { minimumFractionDigits: 2 });

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
    >
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Sticky Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="sticky top-0 z-40 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mb-8"
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate("/expenses")}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                >
                  <Icon name="ArrowLeft" size={20} />
                </button>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <div className="w-6 h-6 bg-violet-100 dark:bg-violet-900/20 rounded flex items-center justify-center">
                      <Icon
                        name="ReceiptText"
                        size={13}
                        className="text-violet-600 dark:text-violet-400"
                      />
                    </div>
                    <span className="text-xs font-medium text-violet-600 dark:text-violet-400 uppercase tracking-wide">
                      {t("purchaseExpenseForms.purchaseBill")}
                    </span>
                  </div>
                  <h1 className="text-2xl font-bold text-foreground dark:text-white">
                    {existing
                      ? t("purchaseExpenseForms.editPurchaseBill")
                      : t("purchaseExpenseForms.newPurchaseBill")}
                  </h1>
                  <p className="text-sm text-muted-foreground dark:text-gray-300">
                    {t("purchaseExpenseForms.purchaseBillDescription")}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="md"
                  onClick={() => navigate("/expenses")}
                >
                  {t("purchaseExpenseForms.cancel")}
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  iconName={saving ? "Loader2" : "Save"}
                  onClick={handleSave}
                  disabled={saving}
                  className="shadow-sm"
                >
                  {saving
                    ? t("purchaseExpenseForms.saving")
                    : existing
                      ? t("purchaseExpenseForms.updateBill")
                      : t("purchaseExpenseForms.saveBill")}
                </Button>
              </div>
            </div>

            {/* Step indicators */}
            <div className="flex items-center gap-2">
              {[
                { label: "Vendor & Bill", icon: "Truck" },
                { label: "Expense Type", icon: "Globe" },
                { label: "Line Items", icon: "List" },
                { label: "Summary", icon: "BarChart2" },
              ].map((step, i) => (
                <React.Fragment key={step.label}>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="w-7 h-7 rounded-full bg-violet-100 dark:bg-violet-900/20 flex items-center justify-center">
                      <Icon
                        name={step.icon}
                        size={13}
                        className="text-violet-600 dark:text-violet-400"
                      />
                    </div>
                    <span className="hidden md:block font-medium text-foreground dark:text-white">
                      {step.label}
                    </span>
                  </div>
                  {i < 3 && (
                    <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600 hidden md:block" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3 space-y-6">
            {/* Section 1: Vendor & Bill Details */}
            <SectionCard
              icon="Truck"
              iconBg="bg-violet-100 dark:bg-violet-900/20"
              iconColor="text-violet-600 dark:text-violet-400"
              title="Vendor & Bill Details"
              subtitle="Who issued this bill and when?"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field
                  label="Vendor"
                  required
                  error={errors.vendorId}
                  hint="Which vendor issued this bill"
                >
                  <SelectInput
                    value={form.vendorId}
                    onChange={(e) => {
                      const v = vendors.find(
                        (x) => String(x.id || x._id) === e.target.value,
                      );
                      set("vendorId", e.target.value);
                      set("vendorName", v?.name || "");
                    }}
                  >
                    <option value="">Select vendor</option>
                    {vendors.map((v) => (
                      <option key={v.id || v._id} value={v.id || v._id}>
                        {v.name}
                      </option>
                    ))}
                  </SelectInput>
                </Field>

                <Field label="Category" required error={errors.category}>
                  <SelectInput
                    value={form.category}
                    onChange={(e) => set("category", e.target.value)}
                  >
                    <option value="">Select category</option>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </SelectInput>
                  {errors.category && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.category}
                    </p>
                  )}
                </Field>

                <Field label="Bill Number" required error={errors.billNumber}>
                  <TextInput
                    value={form.billNumber}
                    placeholder="BILL-001"
                    error={errors.billNumber}
                    className="font-mono"
                    onChange={(e) => set("billNumber", e.target.value)}
                  />
                </Field>

                <Field label="Bill Date" required error={errors.billDate}>
                  <TextInput
                    type="date"
                    value={form.billDate}
                    error={errors.billDate}
                    onChange={(e) => set("billDate", e.target.value)}
                  />
                </Field>
              </div>
            </SectionCard>

            {/* Section 2: Expense Type */}
            <SectionCard
              icon="Globe"
              iconBg="bg-blue-100 dark:bg-blue-900/20"
              iconColor="text-blue-600 dark:text-blue-400"
              title="Expense Type"
              subtitle="Is this a domestic or import purchase?"
            >
              {/* Domestic / Import toggle */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                {["domestic", "import"].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      set("expenseType", type);
                      set("importSubtype", "");
                    }}
                    className={`flex items-center gap-3 p-4 rounded-xl border transition-all duration-200 ${
                      form.expenseType === type
                        ? "border-primary bg-primary-50 dark:bg-primary-900/20 text-primary"
                        : "border-gray-200 dark:border-gray-600 hover:border-gray-300 text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Icon
                      name={type === "domestic" ? "Home" : "Globe"}
                      size={20}
                    />
                    <div className="text-start">
                      <div className="font-medium capitalize">{type}</div>
                      <div className="text-xs text-muted-foreground">
                        {type === "domestic"
                          ? "Local purchase"
                          : "International import"}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Import subtypes */}
              <AnimatePresence>
                {form.expenseType === "import" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <Field
                      label="Import Subtype"
                      required
                      error={errors.importSubtype}
                    >
                      <div className="grid grid-cols-3 gap-3">
                        {IMPORT_SUBTYPES.map((sub) => (
                          <button
                            key={sub.value}
                            type="button"
                            onClick={() => set("importSubtype", sub.value)}
                            className={`p-3 rounded-xl border text-sm font-medium transition-all duration-150 ${
                              form.importSubtype === sub.value
                                ? "border-primary bg-primary-50 dark:bg-primary-900/20 text-primary shadow-sm"
                                : "border-gray-200 dark:border-gray-600 text-muted-foreground hover:border-primary/40 hover:bg-muted/50"
                            }`}
                          >
                            {sub.label}
                          </button>
                        ))}
                      </div>
                      {errors.importSubtype && (
                        <p className="text-xs text-red-500 mt-2">
                          {errors.importSubtype}
                        </p>
                      )}
                    </Field>
                  </motion.div>
                )}
              </AnimatePresence>
            </SectionCard>

            {/* Section 3: Line Items */}
            <SectionCard
              icon="List"
              iconBg="bg-primary-100 dark:bg-primary-900/20"
              iconColor="text-primary"
              title="Line Items"
              subtitle="Add products or services from this bill"
              action={
                <Button
                  variant="primary"
                  size="sm"
                  iconName="Plus"
                  onClick={addLine}
                >
                  Add Item
                </Button>
              }
            >
              <div className="space-y-4">
                {errors.lines && (
                  <p className="text-xs text-red-500">{errors.lines}</p>
                )}

                <AnimatePresence>
                  {form.lines.map((line, index) => {
                    const calc = calcLine(line);
                    return (
                      <motion.div
                        key={line.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center">
                              <span className="text-xs font-semibold text-primary">
                                {index + 1}
                              </span>
                            </div>
                            <span className="text-sm font-medium text-foreground dark:text-white">
                              Line Item {index + 1}
                            </span>
                          </div>
                          {form.lines.length > 1 && (
                            <button
                              onClick={() => removeLine(line.id)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            >
                              <Icon name="Trash2" size={14} />
                            </button>
                          )}
                        </div>

                        {/* Description */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                            Description <span className="text-red-500">*</span>
                          </label>
                          <TextInput
                            value={line.description}
                            placeholder="Product or service description"
                            error={errors[`desc_${index}`]}
                            onChange={(e) =>
                              updateLine(line.id, "description", e.target.value)
                            }
                          />
                          {errors[`desc_${index}`] && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors[`desc_${index}`]}
                            </p>
                          )}
                        </div>

                        {/* Grid: Qty / Price / Discount / VAT % / Amount */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                              Qty
                            </label>
                            <TextInput
                              type="number"
                              value={line.qty}
                              min="0"
                              step="1"
                              onChange={(e) =>
                                updateLine(line.id, "qty", e.target.value)
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                              Price (SR) <span className="text-red-500">*</span>
                            </label>
                            <TextInput
                              type="number"
                              value={line.price}
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              error={errors[`price_${index}`]}
                              onChange={(e) =>
                                updateLine(line.id, "price", e.target.value)
                              }
                            />
                            {errors[`price_${index}`] && (
                              <p className="text-xs text-red-500 mt-1">
                                {errors[`price_${index}`]}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                              Discount
                            </label>
                            <div className="flex gap-1">
                              <TextInput
                                type="number"
                                value={line.discount}
                                min="0"
                                placeholder="0"
                                onChange={(e) =>
                                  updateLine(
                                    line.id,
                                    "discount",
                                    e.target.value,
                                  )
                                }
                                className="flex-1"
                              />
                              <button
                                onClick={() =>
                                  updateLine(
                                    line.id,
                                    "discountType",
                                    line.discountType === "percent"
                                      ? "fixed"
                                      : "percent",
                                  )
                                }
                                className="px-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-xs text-muted-foreground hover:bg-gray-50 transition-colors flex-shrink-0"
                              >
                                {line.discountType === "percent" ? "%" : "SR"}
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                              Sales Tax / VAT
                            </label>
                            <SelectInput
                              value={line.vatRate}
                              onChange={(e) =>
                                updateLine(
                                  line.id,
                                  "vatRate",
                                  e.target.value === "custom"
                                    ? "custom"
                                    : parseFloat(e.target.value),
                                )
                              }
                            >
                              {VAT_OPTIONS.map((v) => (
                                <option key={v.label} value={v.value}>
                                  {v.label}
                                </option>
                              ))}
                            </SelectInput>
                            {line.showCustomVat && (
                              <TextInput
                                type="number"
                                value={line.customVat}
                                min="0"
                                max="100"
                                placeholder="Custom %"
                                onChange={(e) =>
                                  updateLine(
                                    line.id,
                                    "customVat",
                                    e.target.value,
                                  )
                                }
                                className="mt-1 text-xs"
                              />
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                              Amount (SR)
                            </label>
                            <div className="px-3 py-2.5 bg-primary-50 dark:bg-primary-900/20 rounded-lg text-primary font-semibold text-sm">
                              {fmt(calc.total)}
                            </div>
                          </div>
                        </div>

                        {/* Line subtotals */}
                        {parseFloat(line.price) > 0 && (
                          <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground border-t border-gray-200 dark:border-gray-600 pt-3">
                            <span>
                              Subtotal:{" "}
                              <span className="text-foreground font-medium">
                                SR {fmt(calc.subtotal)}
                              </span>
                            </span>
                            {calc.discountAmount > 0 && (
                              <span>
                                Discount:{" "}
                                <span className="text-orange-500 font-medium">
                                  −SR {fmt(calc.discountAmount)}
                                </span>
                              </span>
                            )}
                            {calc.vatAmount > 0 && (
                              <span>
                                VAT ({calc.vatRate}%):{" "}
                                <span className="text-blue-500 font-medium">
                                  SR {fmt(calc.vatAmount)}
                                </span>
                              </span>
                            )}
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </AnimatePresence>

                <div className="flex justify-center pt-2">
                  <Button
                    variant="outline"
                    size="md"
                    iconName="Plus"
                    onClick={addLine}
                    className="border-dashed border-2 hover:border-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 w-full"
                  >
                    Add Another Item
                  </Button>
                </div>
              </div>
            </SectionCard>

            {/* Notes */}
            <SectionCard
              icon="FileText"
              iconBg="bg-gray-100 dark:bg-gray-700"
              iconColor="text-muted-foreground"
              title="Notes"
              subtitle="Any additional information about this bill"
              defaultExpanded={false}
            >
              <textarea
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                placeholder="Additional notes about this purchase bill..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
              />
            </SectionCard>

            {/* Bottom actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 flex justify-end gap-3"
            >
              <Button
                variant="outline"
                size="md"
                onClick={() => navigate("/expenses")}
              >
                {t("purchaseExpenseForms.cancel")}
              </Button>
              <Button
                variant="primary"
                size="md"
                iconName={saving ? "Loader2" : "Save"}
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? t("purchaseExpenseForms.saving")
                  : existing
                    ? t("purchaseExpenseForms.updateBill")
                    : t("purchaseExpenseForms.saveBill")}
              </Button>
            </motion.div>
          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-32 space-y-4">
              {/* Summary */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
              >
                <div className="flex items-center gap-2 mb-5">
                  <Icon name="BarChart2" size={18} className="text-primary" />
                  <h3 className="text-base font-semibold text-foreground dark:text-white">
                    Bill Summary
                  </h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-medium text-foreground">
                      SR{" "}
                      {fmt(lineCalcs.reduce((s, c) => s + c.afterDiscount, 0))}
                    </span>
                  </div>
                  {totalDisc > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Discount</span>
                      <span className="font-medium text-orange-500">
                        −SR {fmt(totalDisc)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">VAT</span>
                    <span className="font-medium text-foreground">
                      SR {fmt(totalVat)}
                    </span>
                  </div>
                  <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
                    <div className="flex justify-between">
                      <span className="font-semibold text-foreground">
                        Net Amount
                      </span>
                      <span className="text-lg font-bold text-primary">
                        SR {fmt(netAmount)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-2 text-xs text-muted-foreground">
                  <div className="flex justify-between">
                    <span>Line Items</span>
                    <span className="font-medium text-foreground">
                      {form.lines.length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Type</span>
                    <span className="font-medium text-foreground capitalize">
                      {form.expenseType}
                      {form.importSubtype
                        ? ` — ${IMPORT_SUBTYPES.find((s) => s.value === form.importSubtype)?.label || ""}`
                        : ""}
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Checklist */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Icon
                    name="CheckCircle2"
                    size={18}
                    className="text-primary"
                  />
                  <h3 className="text-base font-semibold text-foreground dark:text-white">
                    Checklist
                  </h3>
                </div>
                <div className="space-y-2.5">
                  {[
                    { label: "Vendor", ok: !!form.vendorId },
                    { label: "Bill number", ok: !!form.billNumber.trim() },
                    { label: "Bill date", ok: !!form.billDate },
                    { label: "Category", ok: !!form.category },
                    {
                      label: "Import subtype",
                      ok:
                        form.expenseType === "domestic" || !!form.importSubtype,
                    },
                    {
                      label: "Line items",
                      ok:
                        form.lines.length > 0 &&
                        form.lines.every(
                          (l) =>
                            l.description.trim() && parseFloat(l.price) > 0,
                        ),
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Icon
                          name={item.ok ? "CheckCircle2" : "Circle"}
                          size={15}
                          className={
                            item.ok
                              ? "text-green-500"
                              : "text-gray-300 dark:text-gray-600"
                          }
                        />
                        <span className="text-sm text-muted-foreground">
                          {item.label}
                        </span>
                      </div>
                      <span
                        className={`text-xs font-medium ${item.ok ? "text-green-600 dark:text-green-400" : "text-gray-400"}`}
                      >
                        {item.ok ? "Done" : "Pending"}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PurchaseBill = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <PurchaseBillContent />
    </div>
  </SidebarProvider>
);

export default PurchaseBill;
